import { RequestBuilder, fetchAndRetryIfServerBusy } from '@cvent/nucleus-networking';
import { ServiceError } from 'event-widgets/utils/errors';

async function fetchOrThrow(request, errorMessage) {
  const response = await fetchAndRetryIfServerBusy(request);
  if (!response.ok) {
    throw await ServiceError.create(errorMessage, response, request);
  }
  if (response.status === 204) {
    return;
  }
  return await response.json();
}

/**
 * The client to manage a guest for the event.
 */
export default class EventGuestClient {
  constructor(baseUrl, eventId, environment) {
    this.baseUrl = baseUrl;
    this.eventId = eventId;
    this.environment = environment;
  }
  _getRequestBuilder(authToken, urlPath = '') {
    const builder = new RequestBuilder({ url: `${this.baseUrl}${urlPath}` })
      .header('Cvent-Event-ID', this.eventId)
      .auth(authToken)
      .withCookies();
    return this.environment ? builder.query('environment', this.environment) : builder;
  }

  /**
   * Returns the baseUrl for the event guest site
   */
  getBaseUrl = () => this.baseUrl;

  /**
   * Logs out the user dissasociating the user from any saved information in their session.
   */
  async logout(authToken, eventId, userType) {
    const request = this._getRequestBuilder(authToken, `event/${eventId}`)
      .query('userType', userType)
      .delete()
      .build();
    return await fetchOrThrow(request, 'Failed to logout user.');
  }

  /**
   * Opt out or opt in email communications of all events under an give account.
   * @param authToken
   * @param {string} eventId - an event ID from the desired opt out/in account
   * @param {string} inviteeId - invitee ID to identify the user from the event
   */
  async getOptOutStatus(authToken, eventId, inviteeId) {
    let request = this._getRequestBuilder(authToken, `event/${eventId}/opt-in-out`)
      .query('environment', this.environment)
      .query('inviteeId', inviteeId)
      .get();
    return await fetchOrThrow(request.build(), 'Failed to retrieve user opt-out status.');
  }

  /**
   * Opt out or opt in email communications of all events under an give account.
   * @param authToken
   * @param {string} eventId - an event ID from the desired opt out/in account
   * @param {string} inviteeId - invitee ID to identify the user from the event
   * @param {boolean} optOutStatus - desired optOut status. true - opted out.
   */
  async optOut(authToken, eventId, inviteeId, optOutStatus) {
    let request = this._getRequestBuilder(authToken, `event/${eventId}/opt-in-out`)
      .query('environment', this.environment)
      .query('inviteeId', inviteeId)
      .query('optOut', optOutStatus)
      .put();
    return await fetchOrThrow(request.build(), 'Failed to opt-out user.');
  }

  /**
   * Get registration content (pages, layout items, etc.)
   */
  async getRegistrationContent(eventId, snapshotVersion, pageVariety, regPathId = null, registrationTypeId = null) {
    const regPathSegment = regPathId ? `registrationPath/${regPathId}` : 'defaultRegistrationPath';
    const request = this._getRequestBuilder(null, `websiteContent/${eventId}/${regPathSegment}/${pageVariety}`)
      .get()
      .query('snapshotVersion', snapshotVersion)
      .query('registrationTypeId', registrationTypeId)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create(
        `Event snapshot load failed eventId: ${String(eventId)}`, response, request);
    }
    return await response.json();
  }

  /**
   * Get website page content (pages, layout items, etc.)
   */
  async getWebsiteContent(eventId, snapshotVersion, registrationTypeId = null) {
    const request = this._getRequestBuilder(null, `websiteContent/${eventId}/website`)
      .get()
      .query('snapshotVersion', snapshotVersion)
      .query('registrationTypeId', registrationTypeId)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create(
        `Event snapshot load failed eventId: ${String(eventId)}`, response, request);
    }
    return await response.json();
  }
}
